import { Injectable } from '@angular/core';
import { QueryParamsModel } from '../../core/_base/crud';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { FaxTemplate } from '../models';
import { Email } from '../models/email.model';
import { select, Store } from '@ngrx/store';
import { currentUser } from '../../core/auth';
import { AppState } from '../../core/reducers';
import { Inbox } from '../models/inbox.model';
import { CompanyInbox } from '../models/company-inbox.model';
import { TemplateInbox } from '../models/template-inbox.model';
import {
  selectCompanyInboxState,
  selectInboxEmails,
  selectInboxFaxes,
} from '../store/selectors/new-inbox.selector';
import { map, withLatestFrom } from 'rxjs/operators';
import { InboxDetails } from '../models/inbox-details.model';
import { selectInboxAssignees } from '../store/selectors/inbox-app.selectors';

@Injectable({ providedIn: 'root' })
export class FaxEmailService {
  private baseURL = environment.TIMELINE_BASE;
  provisionNumbersParams = new BehaviorSubject<any>({
    region: {},
    country: '',
    areaCode: '',
    prefixInput: '',
    prefixList: '',
  });
  inboxDetailsParamsInitialValue = {
    inboxId: '',
    type: '',
    searchTerm: '',
    assignedToMe: false,
    sentBy: '',
    assignedTo: '',
    fromDate: '',
    toDate: '',
    archived: false,
  };
  inboxDetailsParams = new BehaviorSubject<any>(this.inboxDetailsParamsInitialValue);

  companyId;
  companyInbox;
  assignees;
  private user: any;
  constructor(private httpClient: HttpClient, private store: Store<AppState>) {
    this.store.pipe(select(currentUser)).subscribe((res) => {
      this.companyId = res?.companyID;
      this.user = res;
    });
    this.store.pipe(select(selectCompanyInboxState)).subscribe((res) => {
      this.companyInbox = res;
    });
    this.store.pipe(select(selectInboxAssignees)).subscribe((res) => {
      this.assignees = res;
    });
  }
  private getInboxDetailsQueryString(
    queryParams: QueryParamsModel,
    inboxCategory?: string,
  ): string {
    const {
      pageNumber,
      pageSize = 7,
      sortField = 'created',
      sortOrder = 'desc',
      filter,
    } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter?.inboxId) fromString += `&inbox_id=${filter.inboxId}`;
    if (filter?.type) fromString += `&communication_type=${filter.type}`;
    if (filter?.searchTerm) fromString += `&search_term=${filter.searchTerm}`;
    if (filter?.assignedToMe) {
      fromString += `&assignee_ids=${this.user.id}`;
    } else if (filter?.assignedTo) {
      fromString += `&assignee_ids=${filter.assignedTo}`;
    }
    const senderType = inboxCategory === 'sent' ? 'USER' : 'CONTACT';
    if (filter?.sentBy) fromString += `&sender_id=${filter.sentBy}&sender_type=${senderType}`;
    if (filter?.fromDate) fromString += `&from_date=${filter.fromDate}`;
    if (filter?.toDate) fromString += `&to_date=${filter.toDate}`;
    fromString += `&archived=${filter.archived}`;
    return fromString;
  }

  private static getEmailQueryString(queryParams: QueryParamsModel): string {
    const {
      pageNumber = '',
      pageSize = '',
      sortField = '',
      sortOrder = '',
      filter = '',
    } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) fromString += `&search_term=${filter.searchTerm}`;
    if (filter.emailAddress) fromString += `&email_address=${filter.emailAddress}`;
    if (filter?.provider) fromString += `&provider=${filter.provider}`;
    if (filter?.availableToLink) fromString += `&available_to_link=${filter.availableToLink}`;
    return fromString;
  }

  private static getInboxQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) fromString += `&search_term=${filter.searchTerm}`;
    if (filter.name) fromString += `&name=${filter.name}`;
    if (filter.description) fromString += `&description=${filter.description}`;
    if (filter.status) fromString += `&is_active=${filter.status}`;
    if (filter?.type) fromString += `&type=${filter.type}`;

    return fromString;
  }

  private getFaxQueryString(queryParams: QueryParamsModel): string {
    const {
      pageNumber = '',
      pageSize = '',
      sortField = '',
      sortOrder = '',
      filter = '',
    } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) fromString += `&search_term=${filter.searchTerm}`;
    if (filter.number) fromString += `&number=${filter.number}`;
    if (filter?.coverPage?.id) fromString += `&cover_page_id=${filter.coverPage.id}`;
    if (filter.status) fromString += `&is_active=${filter.status}`;
    if (filter?.availableToLink) fromString += `&available_to_link=${filter.availableToLink}`;
    fromString += `&company_id=${
      this.user.userType === 'Super Admin' ? filter.company?.id || '' : this.companyId
    }`;

    return fromString;
  }

  getFaxList(queryParams: QueryParamsModel): Observable<{ data: FaxTemplate[]; count: number }> {
    const params = new HttpParams({ fromString: this.getFaxQueryString(queryParams) });
    const apiCall$ = this.httpClient.get<{ data: FaxTemplate[]; count: number }>(
      `${this.baseURL}/faxes`,
      { params },
    );

    return apiCall$.pipe(
      withLatestFrom(this.store.select(selectInboxFaxes)), // your selector here
      map(([response, currentFaxes]) => {
        const existingIds = new Set(currentFaxes.items.map((fax) => fax.id));
        const filteredData = response.data.filter((fax) => !existingIds.has(fax.id));
        return { data: filteredData, count: filteredData.length };
      }),
    );
  }

  searchFax(filter: any, searchTerm: string) {
    const queryParams = new QueryParamsModel({ ...filter, searchTerm });
    return this.getFaxList(queryParams);
  }

  getRegions(term: string): Observable<any> {
    if (term?.length >= 3) {
      const params = new HttpParams().set('search_term', term); // Set query param
      return this.httpClient.get(`${this.baseURL}/regions`, { params });
    } else {
      return of({ data: [] });
    }
  }

  getProvisionNumbers(params: any): Observable<any> {
    let httpParams = new HttpParams();
    switch (params?.searchType.value) {
      case 'region':
        httpParams = httpParams
          .set('state', params?.region?.state) // Selected region [province]
          .set('city', params?.region?.city) // Selected region [city]
          .set('country', params?.country?.value);
        break;

      case 'prefix':
        httpParams = httpParams
          .set('area_code', params?.areaCode) // Entered area code
          .set('prefix', params?.prefixInput) // Entered prefix
          .set('country', params?.country?.value);
        break;

      case 'tollfree':
        if (params?.prefixList.value === -1) {
          //in case of 'any' in prefix of toll free
          return this.httpClient.post(`${this.baseURL}/faxes/toll_free`, { quantity: 1 });
        }
        httpParams = httpParams
          .set('area_code', params?.prefixList.value) // Selected prefix for toll-free
          .set('country', 'US');
        break;
    }
    return this.httpClient.get(`${this.baseURL}/faxes/available_numbers`, { params: httpParams });
  }

  orderProvisionNumber(numbers) {
    const body = {
      numbers: [numbers.toString()],
    };
    return this.httpClient.post(`${this.baseURL}/faxes`, body);
  }
  findCoverPages() {
    return this.httpClient.get(`${this.baseURL}/faxes/cover_pages`);
  }

  getFaxbyId(id: number): Observable<any> {
    return this.httpClient.get(`${this.baseURL}/faxes/${id}`);
  }

  editFax(fax: FaxTemplate) {
    const body = {
      company_id: fax?.company?.id,
      cover_page_id: fax?.cover_page?.id,
      is_active: fax?.is_active,
    };
    return this.httpClient.put(`${this.baseURL}/faxes/${fax.id}`, body);
  }

  toggleFaxStatus(fax: FaxTemplate) {
    return this.httpClient.put<void>(`${this.baseURL}/faxes/${fax.id}/status`, {
      is_active: !fax.is_active,
    });
  }

  deleteFax(fax: FaxTemplate) {
    return this.httpClient.delete(`${this.baseURL}/faxes/${fax.id}`);
  }

  getEmailList(queryParams: QueryParamsModel) {
    const params = new HttpParams({ fromString: FaxEmailService.getEmailQueryString(queryParams) });
    const apiCall$ = this.httpClient.get<{ data: Email[]; count: number }>(
      `${this.baseURL}/companies/${this.companyId}/emails`,
      { params },
    );

    return apiCall$.pipe(
      withLatestFrom(this.store.select(selectInboxEmails)), // your selector here
      map(([response, currentEmails]) => {
        const existingIds = new Set(currentEmails.items.map((email) => email.id));
        const filteredData = response.data.filter((email) => !existingIds.has(email.id));
        return { data: filteredData, count: filteredData.length };
      }),
    );
  }

  searchEmail(filter: any, searchTerm: string) {
    const queryParams = new QueryParamsModel({ ...filter, searchTerm });
    return this.getEmailList(queryParams);
  }

  authenticateToNylas(code, provider): Observable<any> {
    const body = {
      code: code,
      provider: provider.toLowerCase(),
    };
    // tslint:disable-next-line:triple-equals
    return this.httpClient.post(
      this.baseURL + `/companies/${this.companyId}/emails/authenticate_to_nylas`,
      body,
    );
  }
  // INBOX LIST
  getInboxList(queryParams: QueryParamsModel): Observable<{ data: Inbox[]; count: number }> {
    const params = new HttpParams({ fromString: FaxEmailService.getInboxQueryString(queryParams) });
    return this.httpClient.get<{ data: Inbox[]; count: number }>(
      `${this.baseURL}/companies/${this.companyId}/inboxes_v2`,
      { params },
    );
  }

  filterInboxList(searchTerm: string) {
    const queryParams = new QueryParamsModel({ searchTerm }, 'desc', 'id', 0, 20);
    return this.getInboxList(queryParams);
  }
  getInboxEmailList(inboxId): Observable<{ data: Email[] }> {
    return this.httpClient.get<{ data: Email[] }>(
      `${this.baseURL}/companies/${this.companyId}/inboxes_v2/${inboxId}/inbox_emails`,
    );
  }

  getInboxFaxList(inboxId): Observable<{ data: FaxTemplate[] }> {
    return this.httpClient.get<{ data: FaxTemplate[] }>(
      `${this.baseURL}/companies/${this.companyId}/inboxes_v2/${inboxId}/inbox_faxes`,
    );
  }

  createCompanyInbox(newInbox: CompanyInbox): Observable<any> {
    return this.httpClient.post(`${this.baseURL}/companies/${this.companyId}/inboxes_v2`, newInbox);
  }
  updateCompanyInbox(updatedInbox: CompanyInbox): Observable<any> {
    return this.httpClient.put(
      `${this.baseURL}/companies/${this.companyId}/inboxes_v2/${this.companyInbox?.id}`,
      updatedInbox,
    );
  }

  getInboxTemplates(inboxId): Observable<{ data: TemplateInbox[] }> {
    return this.httpClient.get<{ data: TemplateInbox[] }>(
      `${this.baseURL}/companies/${this.companyId}/inboxes_v2/${inboxId}/inbox_templates`,
    );
  }

  addNewTemplateToInbox(templates: TemplateInbox[]): Observable<any> {
    const payload = { templates };
    return this.httpClient.put(
      `${this.baseURL}/companies/${this.companyId}/inboxes_v2/${this.companyInbox.id}/inbox_template`,
      payload,
    );
  }

  getInboxbyId(id: number): Observable<any> {
    return this.httpClient.get(`${this.baseURL}/companies/${this.companyId}/inboxes_v2/${id}`);
  }

  deleteInbox(inbox: Inbox) {
    return this.httpClient.delete(
      `${this.baseURL}/companies/${this.companyId}/inboxes_v2/${inbox.id}`,
    );
  }
  // INBOX APP

  getInboxMenu(): Observable<any> {
    return this.httpClient.get(
      `${this.baseURL}/companies/${this.companyId}/inboxes_v2/user_inboxes`,
    );
  }

  getInboxDocuments(inbox, inboxCategory: string): Observable<any> {
    if (inboxCategory === 'sent') {
      return this.httpClient.get(
        `${this.baseURL}/companies/${this.companyId}/communication/${inbox?.id}/attachments`,
      );
    } else {
      return this.httpClient.get(
        `${this.baseURL}/companies/${this.companyId}/received_communication/${inbox?.id}/attachments`,
      );
    }
  }

  getInboxDetails(queryParams: QueryParamsModel, inboxCategory) {
    const params = new HttpParams({
      fromString: this.getInboxDetailsQueryString(queryParams, inboxCategory),
    });
    if (inboxCategory === 'sent') {
      return this.httpClient.get<{ data: InboxDetails[] }>(
        `${this.baseURL}/companies/${this.companyId}/communication`,
        { params },
      );
    } else {
      return this.httpClient.get<{ data: InboxDetails[] }>(
        `${this.baseURL}/companies/${this.companyId}/received_communication`,
        { params },
      );
    }
  }
  filterInboxDetails(filter, inboxCategory, pageNumber) {
    const queryParams = new QueryParamsModel({ ...filter }, 'desc', 'created', pageNumber, 20);
    return this.getInboxDetails(queryParams, inboxCategory);
  }
  archiveInboxes(inbox, inboxCategory) {
    const body = {
      ids: [inbox.id],
      archive: true,
    };
    if (inboxCategory === 'sent') {
      return this.httpClient.put(
        `${this.baseURL}/companies/${this.companyId}/communication/archive`,
        body,
      );
    } else {
      return this.httpClient.put(
        `${this.baseURL}/companies/${this.companyId}/received_communication/archive`,
        body,
      );
    }
  }
  getCompaniesInboxes(type, searchTerm, isNewMessage?: boolean) {
    let params = new HttpParams().set('type', type).set('search_term', searchTerm);
    if (isNewMessage) {
      params = params.append('type', 'BOTH');
    }
    return this.httpClient.get(`${this.baseURL}/companies/${this.companyId}/inboxes_v2`, {
      params,
    });
  }
  sendInboxMessage(body) {
    return this.httpClient.post(
      `${this.baseURL}/companies/${this.companyId}/communication?communication_type=${body.communication_type.value}`,
      body,
    );
  }

  uploadFiles(url: string, file: File): Observable<HttpResponse<void>> {
    return this.httpClient.put<void>(url, file, { observe: 'response' });
  }

  saveAttachments(
    communicationId,
    presignedUrls,
    isReply: boolean,
    replyId?: number,
    inboxCatgeory?: string,
  ) {
    const body = {
      uuids: presignedUrls,
    };
    if (isReply) {
      if (inboxCatgeory === 'sent') {
        return this.httpClient.post(
          `${this.baseURL}/companies/${this.companyId}/communication/${communicationId}/replies/${replyId}/attachments`,
          body,
        );
      } else {
        return this.httpClient.post(
          `${this.baseURL}/companies/${this.companyId}/received_communication/${communicationId}/replies/${replyId}/attachments`,
          body,
        );
      }
    }
    return this.httpClient.post(
      `${this.baseURL}/companies/${this.companyId}/communication/${communicationId}/attachments`,
      body,
    );
  }
  sendInboxReplyMessage(inboxCategory, messageId, body) {
    if (inboxCategory === 'sent') {
      return this.httpClient.post(
        `${this.baseURL}/companies/${this.companyId}/communication/${messageId}/replies`,
        body,
      );
    } else {
      return this.httpClient.post(
        `${this.baseURL}/companies/${this.companyId}/received_communication/${messageId}/replies`,
        body,
      );
    }
  }
  markInboxasSeen(inboxId) {
    const body = {
      ids: [inboxId],
      seen: true,
    };
    return this.httpClient.put(
      `${this.baseURL}/companies/${this.companyId}/received_communication/seen`,
      body,
    );
  }
  updateInboxStatus(inbox) {
    return this.httpClient.put(
      `${this.baseURL}/companies/${this.companyId}/inboxes_v2/${inbox.id}/status`,
      { is_active: !inbox.is_active },
    );
  }
  assignUsersToEmail(selctedAssignees, receivedId, remove) {
    const existingIds = this.assignees ? this.assignees.map((a) => a.id) : [];
    const selectedId = selctedAssignees?.id;

    let userIds: number[];

    if (remove) {
      userIds = existingIds.filter((id) => id !== selectedId);
    } else {
      userIds = Array.from(new Set([...existingIds, selectedId]));
    }

    return this.httpClient.put(
      `${this.baseURL}/companies/${this.companyId}/received_communication/${receivedId}/assignees`,
      { user_ids: userIds },
    );
  }
}
